import React from "react";
import { Box, Typography, Paper, IconButton, useTheme, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

interface WeightInfoBoxProps {
  onClose: () => void;
}

const WeightInfoBox: React.FC<WeightInfoBoxProps> = ({ onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Paper
      sx={{
        p: 0,
        borderRadius: 2,
        border: "1px solid #E0E4E8",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
        mb: 2,
        overflow: "hidden",
      }}
    >
      
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: { xs: 1.5, sm: 2 },
          borderBottom: "1px solid #E0E4E8",
        }}
      >
        <Typography
          variant="h6"
          fontWeight={600}
          fontSize={{ xs: "14px", sm: "16px" }}
        >
          How criteria weight works?
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#656E81", p: 0.5 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      
      <Box
        sx={{
          backgroundColor: "#9198E5",
          backgroundImage: "linear-gradient(135deg, #9198E5 0%, #A5ABF5 100%)",
          color: "white",
          height: { xs: "150px", sm: "200px" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          px: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          variant="body2"
          sx={{ position: "absolute", top: 16, left: 16, fontSize: "12px" }}
        >
          Part 1
        </Typography>
        <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: "14px", sm: "16px" } }}>
          How to get started
        </Typography>
        <Box
          sx={{
            width: "50px",
            height: "50px",
            borderRadius: "50%",
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <PlayCircleOutlineIcon sx={{ fontSize: { xs: 30, sm: 40 } }} />
        </Box>
      </Box>

      
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography
          variant="h6"
          fontWeight={600}
          fontSize={{ xs: "14px", sm: "16px" }}
          sx={{ mb: 2 }}
        >
          AI Instructions for your Job
        </Typography>

        <Typography fontSize="14px" color="#4A5568" sx={{ mb: 2 }}>
          AI can help streamline job descriptions by optimizing wording,
          ensuring clarity, and aligning responsibilities with industry
          standards.
        </Typography>

        {[
          {
            title: "Step 1: Understanding Weights:",
            points: [
              {
                icon: "📝",
                text: `Weights distribute importance across different hiring criteria.
                  Higher weight = more impact on candidate ranking.`,
              },
            ],
          },
          {
            title: "Step 2: How the AI Matches Candidates:",
            points: [
              { icon: "✅", text: "Candidate's profile is scored against each weighted factor" },
              { icon: "✅", text: "Final score is a weighted sum of all factors" },
            ],
          },
          {
            title: "Step 3: Example Calculation:",
            points: [
              { icon: "✅", text: "Skills (50%) – Candidate matches 90% → Earns 45 points" },
              { icon: "✅", text: "Experience (30%) – Candidate matches 70% → Earns 21 points" },
              { icon: "➕", text: "Final Fit Score = 45 + 21 + 12 = 78% fit" },
            ],
          },
        ].map((step, i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Typography fontSize="14px" color="#4A5568" sx={{ mb: 1 }}>
              <Box component="span" sx={{ color: "#E53E3E", fontWeight: "600", mr: 1 }}>
                📌
              </Box>
              <Box component="span" sx={{ fontWeight: "600" }}>
                {step.title}
              </Box>
            </Typography>
            {step.points.map((point, j) => (
              <Typography
                key={j}
                fontSize="14px"
                color="#4A5568"
                sx={{ pl: 3, mb: 0.5, display: "flex", alignItems: "flex-start" }}
              >
                <Box component="span" sx={{ mr: 1 }}>
                  {point.icon}
                </Box>
                {point.text}
              </Typography>
            ))}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default WeightInfoBox;
